import { Component, OnInit, OnDestroy } from '@angular/core'
import { collection, getDocs, query, where, orderBy, limit } from '@angular/fire/firestore'
import { Subscription as RxSubscription } from 'rxjs'
import { EnterpriseContextService } from '../../../services/enterprise-context.service'
import { AdminService } from '../../../services/admin.service'
import { Invoice, InvoiceStatus, Payout, PayoutStatusEnum, PayoutBatch, PayoutBatchStatusEnum } from '@parkupp/core'

interface InvoiceSummary {
    status: InvoiceStatus
    count: number
    totalAmount: number
}

interface PayoutSummary {
    status: PayoutStatusEnum
    count: number
    totalAmount: number
}

@Component({
    selector: 'app-admin-enterprise-accounts-page',
    templateUrl: './admin-enterprise-accounts-page.component.html',
    styleUrls: ['./admin-enterprise-accounts-page.component.scss'],
})
export class AdminEnterpriseAccountsPageComponent implements OnInit, OnDestroy {
    private subscriptions: RxSubscription[] = []
    
    loading = false
    activeTab = 'invoices'
    
    // Invoice Summary
    invoiceSummaries: InvoiceSummary[] = []
    totalInvoicesCount = 0
    totalInvoicesAmount = 0
    recentInvoices: Invoice[] = []
    
    // Pro Forma Invoices (due invoices that haven't been paid)
    proformaInvoices: Invoice[] = []
    proformaTotal = 0
    
    // Payouts
    payoutSummaries: PayoutSummary[] = []
    recentPayouts: Payout[] = []
    totalPayoutsCount = 0
    totalPayoutsAmount = 0
    
    // Payout Batches
    payoutBatches: PayoutBatch[] = []
    
    // Enums for template
    InvoiceStatus = InvoiceStatus
    PayoutStatusEnum = PayoutStatusEnum
    PayoutBatchStatusEnum = PayoutBatchStatusEnum

    constructor(
        public enterpriseContext: EnterpriseContextService,
        private adminService: AdminService
    ) {}

    ngOnInit(): void {
        // Subscribe to enterprise context changes
        const entSub = this.enterpriseContext.currentEnterpriseKey$.subscribe(() => {
            this.loadAllData()
        })
        this.subscriptions.push(entSub)
        
        // Initial load
        this.loadAllData()
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe())
    }

    setTab(tab: string): void {
        this.activeTab = tab
    }

    async loadAllData(): Promise<void> {
        const enterpriseKey = this.enterpriseContext.currentEnterpriseKey
        
        if (!enterpriseKey) {
            this.resetData()
            return
        }
        
        this.loading = true
        
        try {
            await Promise.all([
                this.loadInvoiceSummary(enterpriseKey),
                this.loadProformaInvoices(enterpriseKey),
                this.loadPayouts(enterpriseKey),
                this.loadPayoutBatches(enterpriseKey),
            ])
        } catch (error) {
            console.error('[Accounts] Error loading data:', error)
        } finally {
            this.loading = false
        }
    }

    private resetData(): void {
        this.invoiceSummaries = []
        this.totalInvoicesCount = 0
        this.totalInvoicesAmount = 0
        this.recentInvoices = []
        this.proformaInvoices = []
        this.proformaTotal = 0
        this.payoutSummaries = []
        this.recentPayouts = []
        this.totalPayoutsCount = 0
        this.totalPayoutsAmount = 0
        this.payoutBatches = []
    }

    private async loadInvoiceSummary(enterpriseKey: string): Promise<void> {
        const invoicesQuery = query(
            collection(this.adminService.firestore, 'invoices'),
            where('denormEnterpriseKey', '==', enterpriseKey),
            orderBy('createdAt', 'desc')
        )
        
        const snapshot = await getDocs(invoicesQuery)
        
        // Group by status
        const statusGroups = new Map<InvoiceStatus, { count: number; total: number }>()
        
        // Initialize all statuses
        Object.values(InvoiceStatus).forEach(status => {
            statusGroups.set(status, { count: 0, total: 0 })
        })
        
        snapshot.docs.forEach(doc => {
            const invoice = doc.data() as Invoice
            invoice.$key = doc.id
            const status = invoice.status || InvoiceStatus.DUE
            const group = statusGroups.get(status)!
            group.count++
            group.total += invoice.amountTotalDue || 0
        })
        
        // Convert to array
        this.invoiceSummaries = []
        statusGroups.forEach((data, status) => {
            if (data.count > 0) {
                this.invoiceSummaries.push({
                    status,
                    count: data.count,
                    totalAmount: data.total,
                })
            }
        })
        
        // Calculate totals
        this.totalInvoicesCount = snapshot.docs.length
        this.totalInvoicesAmount = this.invoiceSummaries.reduce((sum, s) => sum + s.totalAmount, 0)
        
        // Get recent invoices
        this.recentInvoices = snapshot.docs.slice(0, 10).map(doc => {
            const invoice = new Invoice(doc.data() as Partial<Invoice>, doc.id)
            return invoice
        })
    }

    private async loadProformaInvoices(enterpriseKey: string): Promise<void> {
        const proformaQuery = query(
            collection(this.adminService.firestore, 'invoices'),
            where('denormEnterpriseKey', '==', enterpriseKey),
            where('status', '==', InvoiceStatus.DUE),
            orderBy('dueDate', 'asc'),
            limit(50)
        )
        
        const snapshot = await getDocs(proformaQuery)
        
        this.proformaInvoices = snapshot.docs.map(doc => {
            const invoice = new Invoice(doc.data() as Partial<Invoice>, doc.id)
            return invoice
        })
        
        this.proformaTotal = this.proformaInvoices.reduce((sum, inv) => sum + (inv.amountTotalDue || 0), 0)
    }

    private async loadPayouts(enterpriseKey: string): Promise<void> {
        const payoutsQuery = query(
            collection(this.adminService.firestore, 'payouts'),
            where('denormEnterpriseKey', '==', enterpriseKey),
            orderBy('createdAt', 'desc'),
            limit(50)
        )
        
        try {
            const snapshot = await getDocs(payoutsQuery)
            
            // Group by status
            const statusGroups = new Map<PayoutStatusEnum, { count: number; total: number }>()
            
            Object.values(PayoutStatusEnum).forEach(status => {
                statusGroups.set(status, { count: 0, total: 0 })
            })
            
            snapshot.docs.forEach(doc => {
                const payout = doc.data() as Payout
                payout.$key = doc.id
                const status = payout.status || PayoutStatusEnum.DUE
                const group = statusGroups.get(status)
                if (group) {
                    group.count++
                    group.total += payout.payoutAmount || 0
                }
            })
            
            this.payoutSummaries = []
            statusGroups.forEach((data, status) => {
                if (data.count > 0) {
                    this.payoutSummaries.push({
                        status,
                        count: data.count,
                        totalAmount: data.total,
                    })
                }
            })
            
            this.totalPayoutsCount = snapshot.docs.length
            this.totalPayoutsAmount = this.payoutSummaries.reduce((sum, s) => sum + s.totalAmount, 0)
            
            this.recentPayouts = snapshot.docs.slice(0, 10).map(doc => {
                return new Payout(doc.data() as Partial<Payout>, doc.id)
            })
        } catch (error) {
            // Payouts may not have denormEnterpriseKey yet
            console.warn('[Accounts] Payouts query failed (may need migration):', error)
            this.payoutSummaries = []
            this.recentPayouts = []
            this.totalPayoutsCount = 0
            this.totalPayoutsAmount = 0
        }
    }

    private async loadPayoutBatches(enterpriseKey: string): Promise<void> {
        // Note: Payout batches may not have enterprise key - load recent ones
        // In future, this should be filtered by enterprise
        const batchesQuery = query(
            collection(this.adminService.firestore, 'payoutBatches'),
            orderBy('createdAt', 'desc'),
            limit(20)
        )
        
        try {
            const snapshot = await getDocs(batchesQuery)
            this.payoutBatches = snapshot.docs.map(doc => {
                return new PayoutBatch(doc.data() as Partial<PayoutBatch>, doc.id)
            })
        } catch (error) {
            console.warn('[Accounts] Payout batches query failed:', error)
            this.payoutBatches = []
        }
    }

    getStatusBadgeClass(status: InvoiceStatus): string {
        switch (status) {
            case InvoiceStatus.PAID:
                return 'bg-success'
            case InvoiceStatus.DUE:
                return 'bg-warning text-dark'
            case InvoiceStatus.CANCELLED:
            case InvoiceStatus.REFUNDED:
            case InvoiceStatus.BREECHED:
                return 'bg-danger'
            default:
                return 'bg-secondary'
        }
    }

    getPayoutStatusBadgeClass(status: PayoutStatusEnum): string {
        switch (status) {
            case PayoutStatusEnum.PAID:
                return 'bg-success'
            case PayoutStatusEnum.DUE:
                return 'bg-warning text-dark'
            case PayoutStatusEnum.BATCHED:
                return 'bg-info'
            case PayoutStatusEnum.DELETED:
                return 'bg-danger'
            default:
                return 'bg-secondary'
        }
    }

    getPayoutBatchStatusBadgeClass(status: PayoutBatchStatusEnum): string {
        switch (status) {
            case PayoutBatchStatusEnum.PAID:
                return 'bg-success'
            case PayoutBatchStatusEnum.DUE:
                return 'bg-warning text-dark'
            case PayoutBatchStatusEnum.DELETED:
                return 'bg-danger'
            default:
                return 'bg-secondary'
        }
    }

    formatCurrency(amount: number): string {
        return new Intl.NumberFormat('en-ZA', {
            style: 'currency',
            currency: 'ZAR',
        }).format(amount)
    }

    formatDate(timestamp: any): string {
        if (!timestamp) return '-'
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
        return date.toLocaleDateString('en-ZA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    }

    viewInvoice(invoiceKey: string): void {
        window.open(`/admin/invoices?search=${invoiceKey}`, '_blank')
    }

    viewPayout(payoutKey: string): void {
        window.open(`/admin/payout/${payoutKey}`, '_blank')
    }

    viewPayoutBatch(batchKey: string): void {
        window.open(`/admin/payout-batch/${batchKey}`, '_blank')
    }
}

